import React, { useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, LogIn, Glasses, ShieldCheck, FlaskConical, Copy } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = ({ onLogin }) => {
  const [usuario, setUsuario] = useState("");
  const [senha, setSenha] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testForm, setTestForm] = useState({ nome: "", email: "", telefone: "", cpf: "" });
  const [testCred, setTestCred] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  const criarTeste = async () => {
    const { nome, email, telefone, cpf } = testForm;
    if (!nome || !email || !telefone || !cpf) return toast.error("Preencha todos os campos");
    try {
      setTestLoading(true);
      const res = await axios.post(`${API}/auth/test-account`, testForm);
      setTestOpen(false);
      setTestCred({ login: res.data.login, senha: res.data.senha, expira_em: res.data.expira_em });
      setTestForm({ nome: "", email: "", telefone: "", cpf: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar conta de teste");
    } finally {
      setTestLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!usuario || !senha) {
      return toast.error("Preencha usuario e senha");
    }

    try {
      setLoading(true);
      const res = await axios.post(`${API}/auth/login`, { usuario, senha });
      
      if (res.data.success) {
        localStorage.setItem("auth_token", res.data.token);
        localStorage.setItem("auth_usuario", res.data.usuario);
        localStorage.setItem("auth_expira", res.data.expira_em);
        axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
        toast.success("Login realizado com sucesso!");
        onLogin(res.data);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || "Erro ao fazer login";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white/95 backdrop-blur shadow-2xl border-0">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-slate-900 rounded-full flex items-center justify-center mb-4">
            <Glasses className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900">
            Óticas Laurindo
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Sistema de Gestão
          </p>
        </CardHeader>

        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="usuario" className="text-slate-700">
                Usuário
              </Label>
              <Input
                id="usuario"
                type="text"
                value={usuario}
                onChange={(e) => setUsuario(e.target.value)}
                placeholder="Digite seu usuario"
                className="h-11 border-slate-300 focus:border-slate-500"
                data-testid="input-usuario"
                autoComplete="username"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="senha" className="text-slate-700">
                Senha
              </Label>
              <div className="relative">
                <Input
                  id="senha"
                  type={mostrarSenha ? "text" : "password"}
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  placeholder="Digite sua senha"
                  className="h-11 border-slate-300 focus:border-slate-500 pr-10"
                  data-testid="input-senha"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setMostrarSenha(!mostrarSenha)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {mostrarSenha ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium"
              disabled={loading}
              data-testid="btn-login"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Entrando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <LogIn className="w-4 h-4" />
                  Entrar
                </span>
              )}
            </Button>
          </form>

          <div className="mt-5 pt-5 border-t border-slate-100 text-center">
            <button type="button" onClick={() => setTestOpen(true)} data-testid="btn-criar-teste"
              className="text-sm font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
              <FlaskConical className="w-4 h-4" /> Criar conta de teste (3 dias grátis)
            </button>
          </div>
        </CardContent>
      </Card>

      <button type="button" onClick={() => { window.location.href = "/admin"; }} data-testid="btn-acesso-admin"
        className="fixed bottom-4 left-4 text-xs text-white/50 hover:text-white/90 inline-flex items-center gap-1.5 transition-colors">
        <ShieldCheck className="w-4 h-4" /> Acesso Administrativo
      </button>

      {/* Modal conta de teste */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="bg-white" data-testid="teste-modal">
          <DialogHeader>
            <DialogTitle>Conta de teste — 3 dias grátis</DialogTitle>
            <DialogDescription>Preencha seus dados. Permitido apenas um teste por CPF.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {[["nome", "Nome completo"], ["email", "E-mail"], ["telefone", "Telefone"], ["cpf", "CPF"]].map(([k, label]) => (
              <div key={k} className="space-y-1">
                <Label className="text-slate-600 text-sm">{label}</Label>
                <Input value={testForm[k]} onChange={(e) => setTestForm((f) => ({ ...f, [k]: e.target.value }))} data-testid={`teste-${k}`} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)}>Cancelar</Button>
            <Button className="bg-slate-900 text-white" disabled={testLoading} onClick={criarTeste} data-testid="teste-criar-submit">
              {testLoading ? "Criando..." : "Criar conta de teste"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credenciais do teste geradas */}
      <Dialog open={!!testCred} onOpenChange={(o) => !o && setTestCred(null)}>
        <DialogContent className="bg-white sm:max-w-sm" data-testid="teste-cred-modal">
          <DialogHeader>
            <DialogTitle>Conta de teste criada ✅</DialogTitle>
            <DialogDescription>Anote seu acesso. Válido até {testCred?.expira_em ? new Date(testCred.expira_em).toLocaleDateString("pt-BR") : ""}.</DialogDescription>
          </DialogHeader>
          <div className="bg-slate-50 rounded-lg p-4 text-sm space-y-2">
            <div><span className="text-slate-500">Login:</span> <strong>{testCred?.login}</strong></div>
            <div><span className="text-slate-500">Senha:</span> <strong>{testCred?.senha}</strong></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(`Login: ${testCred?.login}\nSenha: ${testCred?.senha}`); toast.success("Copiado"); }}>
              <Copy className="w-4 h-4 mr-2" /> Copiar
            </Button>
            <Button className="bg-slate-900 text-white" onClick={() => { setUsuario(testCred?.login || ""); setSenha(testCred?.senha || ""); setTestCred(null); }}>Usar agora</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Login;
